import { Injectable, inject } from '@angular/core';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { FinishedPlantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/finished-plant-list.response';
import { HarvestPlantsResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/harvest-plants.response';
import { PlantEndReason } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/plant-end-reason.enum';
import { StorageService } from '../front/storage.service';
import { PosthogService } from '../front/posthog.service';
import { DataStore } from './data-store.service';

/** Le retrait en lot prend 100 plantes au plus. */
const HARVEST_BATCH_MAX = 100;

/**
 * Les cultures qui semblent finies (carte de l'accueil) : la lecture, et leur
 * rangement par le retrait en lot existant, un appel par motif de fin.
 *
 * Proposer, jamais ranger d'office : rien ne part sans un geste de la
 * personne. Le jardin est celui de l'app (en-tête d'adresse du SDK).
 */
@Injectable({ providedIn: 'root' })
export class FinishedPlantsStore {
  // Les classes du SDK lisent leur configuration dans un singleton : on les
  // instancie directement, comme `GardenTodoStore`.
  private readonly plantApi = new PlantApi();
  private readonly data = inject(DataStore);
  private readonly storage = inject(StorageService);
  private readonly posthog = inject(PosthogService);

  /**
   * Relue à chaque ouverture, et pas gardée hors ligne : sans réseau, la
   * carte se tait plutôt que de proposer un rangement qui ne partirait pas.
   */
  list(): Promise<FinishedPlantListResponse[]> {
    const lang = this.storage.getCurrentLanguage();
    return this.data.get(`garden/finished:${lang}`, () => this.plantApi.findFinished(lang), { ttlMs: 0 });
  }

  /**
   * Range ces plantes : archivées, elles restent dans l'historique. Le lot
   * n'est pas transactionnel : ce qui n'est pas parti revient dans `failedUids`,
   * un appel refusé y renvoie tout son lot.
   */
  async tidy(plants: FinishedPlantListResponse[]): Promise<HarvestPlantsResponse> {
    const lang = this.storage.getCurrentLanguage();
    const batches = toBatches(plants);
    const results = await Promise.allSettled(
      batches.map((batch) => this.plantApi.harvestMany({ endReason: batch.endReason, uids: batch.uids }, lang)),
    );
    const archivedUids: string[] = [];
    const failedUids: string[] = [];
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        archivedUids.push(...result.value.archivedUids);
        failedUids.push(...result.value.failedUids);
      } else {
        failedUids.push(...batches[i].uids);
      }
    });
    const reasons = new Map(plants.map((plant) => [plant.uid, plant.endReason]));
    for (const plantUid of archivedUids) {
      this.posthog.capture('plant_ended', { plantUid, endReason: reasons.get(plantUid), source: 'finished_plants' });
    }
    return { archivedUids, failedUids };
  }
}

/** Un appel par motif de fin, et par tranche de `HARVEST_BATCH_MAX`. */
function toBatches(plants: FinishedPlantListResponse[]): { endReason: PlantEndReason; uids: string[] }[] {
  const byReason = new Map<PlantEndReason, string[]>();
  for (const plant of plants) {
    byReason.set(plant.endReason, [...(byReason.get(plant.endReason) ?? []), plant.uid]);
  }
  return [...byReason].flatMap(([endReason, uids]) =>
    Array.from({ length: Math.ceil(uids.length / HARVEST_BATCH_MAX) }, (_, i) => ({
      endReason,
      uids: uids.slice(i * HARVEST_BATCH_MAX, (i + 1) * HARVEST_BATCH_MAX),
    })),
  );
}
